import React, { useState } from 'react';
import { X, Clock, RefreshCw, CheckCheck, FileText, Brain, Target, MessageCircle, TrendingUp, ListChecks, Search, UserCheck, CalendarCheck, BarChart3, Flame, Sun, Snowflake, ShieldCheck, Send, Eye } from 'lucide-react';

interface WhatsAppHowIWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'process' | 'behavior' | 'intelligence';

const processSteps = [
  {
    icon: Search,
    title: 'Receive & Research Leads',
    description: 'I receive your approved leads and research each prospect — their company, role, industry, and recent activity — so every message feels personal and relevant to their world.',
  },
  {
    icon: FileText,
    title: 'Generate Personalized Message',
    description: 'Using your approved templates and the lead\'s profile, I craft a tailored WhatsApp message — from the first outreach to warm follow-ups — designed to start real conversations.',
  },
  {
    icon: Clock,
    title: 'Schedule Optimal Send Time',
    description: 'I calculate the best time to send based on the prospect\'s timezone and business hours, ensuring messages arrive when they\'re most likely to be read and replied to.',
  },
  {
    icon: Send,
    title: 'Send the Message',
    description: 'I deliver the message via your connected WhatsApp Business number, following the approved template sequence while maintaining a natural, human conversation flow.',
  },
  {
    icon: Eye,
    title: 'Detect & Classify Replies',
    description: 'I monitor delivery receipts, read status, and incoming replies. When a prospect responds, I analyze the sentiment and intent to determine the best next action.',
  },
  {
    icon: CalendarCheck,
    title: 'Book Meeting or Schedule Follow-up',
    description: 'If the prospect shows interest, I move to book a meeting directly. Otherwise, I queue the next message in the sequence at the right time and with the right tone.',
  },
  {
    icon: BarChart3,
    title: 'Log & Analyze Results',
    description: 'Every message exchange is logged with a structured summary — delivery status, read receipts, reply content, sentiment, and next steps — giving you full visibility.',
  },
];

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'behavior', label: 'Behavior', icon: Target },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

const WhatsAppHowIWorkModal: React.FC<WhatsAppHowIWorkModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('process');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Brain className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 font-display tracking-tight">How I Work</h3>
              <p className="text-sm text-gray-600 mt-1">WhatsApp outreach process, behavior rules and intelligence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'text-[#1875FF] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center space-x-2">
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'process' && <ProcessTab />}
          {activeTab === 'behavior' && <BehaviorTab />}
          {activeTab === 'intelligence' && <IntelligenceTab />}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

const ProcessTab = () => (
  <div className="space-y-4 animate-in slide-in-from-left duration-300">
    {processSteps.map((step, index) => {
      const Icon = step.icon;
      return (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-[#1875FF]/30 transition-all hover:shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 self-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg">
                <Icon className="text-white" size={24} />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-400 mb-1">
                STEP {index + 1}: <span className="text-gray-900">{step.title}</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const BehaviorTab = () => (
  <div className="space-y-6 animate-in slide-in-from-left duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Clock className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Messaging Hours</h4>
          <p className="text-gray-700 leading-relaxed">
            I send messages during business hours (9 AM - 6 PM) in the prospect's local timezone.
            I never send late-night or early-morning messages — every outreach arrives at a natural,
            professional time to maximize read and reply rates.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <RefreshCw className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Follow-up Sequence Logic</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I follow a structured outreach sequence with smart timing:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">1.</span>
              <span>First outreach message — personalized intro with a clear value hook</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">2.</span>
              <span>Follow-up 1 — gentle nudge if no reply after configurable delay</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">3.</span>
              <span>Follow-up 2 — different angle or social proof approach</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">4.</span>
              <span>Follow-up 3 — final attempt with a soft close or break-up message</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Warm follow-ups: 3 additional templates for prospects who replied then went quiet</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <ShieldCheck className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Template Approval Workflow</h4>
          <p className="text-gray-700 leading-relaxed">
            Every WhatsApp message template goes through an approval workflow before being sent to prospects.
            Templates are submitted for review, and only approved messages enter the active outreach sequence.
            This ensures compliance, quality, and brand consistency across all conversations.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <CheckCheck className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Read Receipt Intelligence</h4>
          <p className="text-gray-700 leading-relaxed">
            I track delivery and read receipts for every message. If a message was delivered but not read,
            I adjust timing for the next attempt. If it was read but not replied to, I adapt the follow-up
            tone and angle to re-engage the prospect.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">When I Stop Messaging</h4>
          <p className="text-gray-700 leading-relaxed">
            I stop messaging when: the prospect explicitly asks me to stop, the maximum follow-up sequence
            is completed, a meeting has been successfully booked, or the lead is marked as unqualified
            by your team.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const IntelligenceTab = () => (
  <div className="space-y-6 animate-in slide-in-from-right duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <MessageCircle className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Reply Sentiment Detection</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I analyze every incoming reply to classify sentiment and intent:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Positive:</strong> Interest shown, questions asked, meeting willingness</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Neutral:</strong> Acknowledgment, request for more info, non-committal</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Negative:</strong> Not interested, stop messaging, wrong person</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Warm re-engagement:</strong> Replied after a long silence, came back interested</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <TrendingUp className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Temperature Classification</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I classify leads into temperature categories based on WhatsApp engagement:
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
              <Flame size={14} className="text-red-500" />
              Hot Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
              <Sun size={14} />
              Warm Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
              <Snowflake size={14} />
              Cold Lead
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Conversation Summarization</h4>
          <p className="text-gray-700 leading-relaxed">
            After each WhatsApp exchange, I create structured summaries extracting key information —
            prospect interest level, questions asked, objections raised, agreed next steps, and overall
            sentiment. You get full context without reading every message thread.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Pattern Learning</h4>
          <p className="text-gray-700 leading-relaxed">
            I continuously learn which message templates get the best reply rates, what sending times
            drive the highest read rates, and which follow-up angles re-engage cold prospects. These
            insights are used to optimize future campaigns automatically.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default WhatsAppHowIWorkModal;
